import type * as React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { strings } from "@/I18n";
import { cn } from "@/lib/utils";
import { TEST_IDS } from "@/v2/constants/testIds";
import { DateTimePicker } from "@/v2/ui/composed/date-picker";
import { Label } from "@/v2/ui/primitives/label";
import { resolveFormDateTimeValue } from "./formDateHelpers";

export { resolveFormDateTimeValue };

export interface FormDateTimePickerProps {
  name: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  disablePast?: boolean;
  disableFuture?: boolean;
  placeholder?: string;
  className?: string;
}

export const FormDateTimePicker: React.FC<FormDateTimePickerProps> = ({
  name,
  label,
  required = false,
  disabled = false,
  disablePast = false,
  disableFuture = false,
  placeholder,
  className,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const error = errors[name];
  const errorMessage = error?.message as string | undefined;
  const resolvedPlaceholder = placeholder ?? strings("common.pickDateTime");

  return (
    <div
      className={cn("tw-space-y-1", className)}
      data-testid={TEST_IDS.formField(name)}
    >
      {label && (
        <Label htmlFor={name}>
          {label}
          {required && <span className="tw-text-destructive tw-ml-1">*</span>}
        </Label>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <DateTimePicker
            id={name}
            value={resolveFormDateTimeValue(field.value)}
            onChange={(date) => {
              field.onChange(date ? date.toISOString() : null);
            }}
            placeholder={resolvedPlaceholder}
            disabled={disabled}
            minDate={disablePast ? new Date() : undefined}
            maxDate={disableFuture ? new Date() : undefined}
            className={cn(
              error &&
                "[&_button]:tw-border-destructive [&_button]:focus-visible:tw-ring-destructive"
            )}
          />
        )}
      />
      {errorMessage && (
        <p
          className="tw-text-sm tw-text-destructive tw-mt-1"
          data-testid={TEST_IDS.formError(name)}
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
};
